<template>
  <AModal
    :model-value="modelValue"
    size="md"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #title>
      <span class="lvp__title">
        <i class="material-icons-round lvp__title-icon">publish</i>
        v{{ version }} 발행
      </span>
    </template>
    <template #sub>{{ documentName }} · {{ documentId }}</template>

    <div class="lvp">
      <!-- 한국어 본문 없이는 서버가 400 으로 막습니다. 아예 발행을 못 누르게 합니다. -->
      <div v-if="koMissing" class="lvp__notice lvp__notice--err">
        <i class="material-icons-round">block</i>
        <span>
          <b>발행할 수 없습니다.</b><br />
          필수 문서는 한국어(ko) 본문이 있어야 발행할 수 있습니다. 초안을 수정해 한국어 본문을 채우세요.
        </span>
      </div>

      <template v-else>
        <!-- 시행 시점 -->
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">event</i>
            시행 시점 <span class="p-field__req">*</span>
          </label>
          <div class="lvp__modes">
            <label :class="['lvp__mode', { 'is-on': mode === 'now' }]">
              <input v-model="mode" type="radio" value="now" />
              <span class="lvp__mode-body">
                <b>즉시 시행</b>
                <small>발행하는 즉시 현행 버전이 되고 앱에 반영됩니다.</small>
              </span>
            </label>
            <label :class="['lvp__mode', { 'is-on': mode === 'schedule' }]">
              <input v-model="mode" type="radio" value="schedule" />
              <span class="lvp__mode-body">
                <b>날짜 지정 (사전 고지)</b>
                <small>지정한 날짜까지는 현행 버전이 유지되고, 시행일에 자동 전환됩니다.</small>
              </span>
            </label>
          </div>
        </div>

        <div v-if="mode === 'schedule'" class="p-field lvp__schedule">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">schedule</i>
            시행일시 (KST) <span class="p-field__req">*</span>
          </label>
          <input
            v-model="effectiveLocal"
            class="p-input"
            type="datetime-local"
            :min="minLocal"
          />
          <div class="lvp__presets">
            <ABtn size="sm" kind="ghost" @click="setPreset(7)">7일 후</ABtn>
            <ABtn size="sm" kind="ghost" @click="setPreset(30)">30일 후</ABtn>
          </div>
          <div class="p-field__hint">
            <i>info</i>
            <template v-if="currentEffectiveAt">
              현행 시행일 {{ fmtKst(currentEffectiveAt) }} 이후만 선택할 수 있습니다. (그 이전 날짜는 서버가 거부합니다)
            </template>
            <template v-else>지금 이후 시각만 선택할 수 있습니다.</template>
          </div>
          <div v-if="scheduleInvalid" class="lvp__err">
            선택할 수 있는 가장 이른 시각은 {{ minLocal.replace('T', ' ') }} 입니다.
          </div>
        </div>

        <!-- 재동의 경고. 차단형 재동의는 '필수 문서'에만 걸립니다(선택·NDA 제외). -->
        <div
          v-if="requiresReconsent"
          :class="['lvp__notice', blocksUsers ? 'lvp__notice--err' : 'lvp__notice--warn']"
        >
          <i class="material-icons-round">groups</i>
          <span v-if="blocksUsers">
            이 버전은 <b>실질 개정</b>으로 등록되어 있습니다.<br />
            필수 문서라서, 발행하면 <b>기존에 동의한 회원 전원</b>이 앱에서
            <b>차단형 재동의 화면</b>을 보게 됩니다.
          </span>
          <span v-else>
            이 버전은 <b>실질 개정</b>으로 등록되어 있습니다.<br />
            다만 {{ nonBlockingReason }}라서 <b>차단형 재동의 화면은 뜨지 않습니다.</b>
            기존 동의자에게는 개정 사실만 안내됩니다.
          </span>
        </div>
        <div v-else class="lvp__notice lvp__notice--info">
          <i class="material-icons-round">info</i>
          <span>경미 개정으로 등록되어 있어 기존 동의자에게 재동의를 요구하지 않습니다.</span>
        </div>

        <!-- 비가역 경고 -->
        <div class="lvp__notice lvp__notice--warn">
          <i class="material-icons-round">warning_amber</i>
          <span>
            <b>발행은 되돌릴 수 없습니다.</b> 발행 후에는 이 버전의 본문을 수정하거나 삭제할 수 없습니다.
            (사용자가 동의한 내용의 증빙을 보존하기 위한 제약입니다)
          </span>
        </div>

        <label class="lvp__confirm">
          <input v-model="confirmed" type="checkbox" />
          <span>위 내용을 확인했으며 v{{ version }} 을 발행합니다.</span>
        </label>
      </template>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="$emit('update:modelValue', false)">취소</ABtn>
      <ABtn
        kind="danger-fill"
        icon="publish"
        :disabled="!canPublish"
        :loading="publishing"
        @click="publish"
      >발행</ABtn>
    </template>
  </AModal>
</template>

<script>
import swal from "sweetalert2";
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { parseUtc } from "@/utils/datetime";
import { fmtKst, toKstInput, kstInputToUtc } from "@/utils/legal";

const MINUTE = 60 * 1000;

export default {
  name: "LegalVersionPublishModal",
  components: { AModal, ABtn },
  props: {
    modelValue: { type: Boolean, default: false },
    documentId: { type: String, required: true },
    documentName: { type: String, default: "" },
    version: { type: String, default: "" },
    requiresReconsent: { type: Boolean, default: false },
    // 필수 문서인데 한국어 본문이 없으면 서버가 400. 버전 목록의 langs 로 미리 판정합니다.
    isRequiredDoc: { type: Boolean, default: false },
    // 차단형 재동의 대상 판정에 씁니다 (nda 는 강사 웹 전용 흐름이라 제외).
    docType: { type: String, default: "" },
    versionLangs: { type: Array, default: () => [] },
    // 백데이팅 방지용. 이미 예약 발행된 미래 버전이 있을 수 있어 발행본 전체를 받습니다.
    publishedEffectiveAts: { type: Array, default: () => [] },
    // 현행 발행본의 시행일 (안내 문구용)
    currentEffectiveAt: { type: String, default: "" },
  },
  emits: ["update:modelValue", "published"],
  data() {
    return {
      mode: "now",
      effectiveLocal: "",
      confirmed: false,
      publishing: false,
    };
  },
  computed: {
    koMissing() {
      return this.isRequiredDoc && !this.versionLangs.includes("ko");
    },
    /**
     * requiresReconsent=true 라도 차단형 재동의 화면이 뜨는 건 '필수 문서'뿐입니다.
     * 선택 동의는 서비스 이용 조건이 될 수 없어 강제하지 않고, NDA 는 강사 웹 전용
     * 흐름이라 학습자 앱의 재동의 집계에서 빠집니다.
     */
    blocksUsers() {
      return this.isRequiredDoc && this.docType !== "nda";
    },
    nonBlockingReason() {
      return this.docType === "nda" ? "강사 보안서약(NDA)" : "선택 동의 문서";
    },
    /**
     * 선택 가능한 가장 이른 시각.
     * 서버는 시행일이 현행 발행본보다 "빠르거나 같으면" 409 이고, HTML min 은 그 값 자체를
     * 허용(inclusive)하므로 1분을 더해 경계값을 피합니다.
     */
    minLocal() {
      const times = this.publishedEffectiveAts
        .map((v) => parseUtc(v))
        .filter(Boolean)
        .map((d) => d.getTime());
      const floor = times.length ? Math.max(...times) + MINUTE : 0;
      return toKstInput(new Date(Math.max(Date.now() + MINUTE, floor)).toISOString());
    },
    scheduleInvalid() {
      if (this.mode !== "schedule") return false;
      if (!this.effectiveLocal) return true;
      return this.effectiveLocal < this.minLocal;
    },
    canPublish() {
      if (this.koMissing || this.publishing || !this.confirmed) return false;
      return !this.scheduleInvalid;
    },
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.mode = "now";
        this.effectiveLocal = "";
        this.confirmed = false;
        this.publishing = false;
      }
    },
  },
  methods: {
    fmtKst,
    setPreset(days) {
      const base = Date.now() + days * 24 * 60 * MINUTE;
      const min = this.minLocal;
      const candidate = toKstInput(new Date(base).toISOString());
      this.effectiveLocal = candidate < min ? min : candidate;
    },
    async publish() {
      if (!this.canPublish) return;
      this.publishing = true;
      try {
        const data =
          this.mode === "schedule" ? { effectiveAt: kstInputToUtc(this.effectiveLocal) } : {};
        const res = await this.$axios({
          method: "post",
          url: `/emp/legal/documents/${encodeURIComponent(this.documentId)}/versions/${encodeURIComponent(this.version)}/publish`,
          data,
          loadingBar: true,
        });

        const effectiveAt = res.data?.effectiveAt;
        this.$notifyVue({
          type: "success",
          title: `v${this.version} 을 발행했습니다.`,
          text:
            this.mode === "schedule"
              ? `${fmtKst(effectiveAt)} 부터 시행됩니다. 그때까지는 현행 버전이 그대로 유지됩니다.`
              : "지금부터 현행 버전입니다. 문서가 '미노출' 상태라면 목록에서 활성화해야 앱에 보입니다.",
        });
        this.$emit("update:modelValue", false);
        this.$emit("published");
      } catch (e) {
        console.error("발행 실패:", e);
        // 400(ko 본문 없음) / 409(draft 아님·백데이팅) 문구는 서버가 사용자용으로 내려줍니다.
        swal.fire("발행 실패", apiErrorMessage(e, "발행하지 못했습니다."), "error");
        this.$emit("published"); // 다른 관리자의 변경일 수 있으므로 목록을 새로 고칩니다
      } finally {
        this.publishing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.lvp {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__title-icon {
    color: $p-point-deep;
    font-size: 20px !important;
  }

  &__modes {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__mode {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 11px 13px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    background: $p-panel;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &.is-on {
      border-color: $p-point-deep;
      background: $p-panel-alt;
    }

    input { margin-top: 3px; flex-shrink: 0; }
  }

  &__mode-body {
    display: flex;
    flex-direction: column;
    gap: 3px;

    b { font-size: 12px; font-weight: 800; color: $p-ink; }
    small { font-size: 11px; line-height: 1.5; color: $p-ink-soft; }
  }

  &__schedule {
    padding: 12px 14px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
  }

  &__presets {
    display: flex;
    gap: 6px;
    margin-top: 8px;
  }

  &__err {
    margin-top: 6px;
    font-size: 11px;
    font-weight: 700;
    color: $p-err;
  }

  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 11px 13px;
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.55;
    color: $p-ink;

    > i { font-size: 18px !important; flex-shrink: 0; }

    &--err {
      background: $p-err-soft;
      border: 1px solid rgba($p-err, 0.3);

      > i { color: $p-err; }
    }

    &--warn {
      background: $p-warn-soft;
      border: 1px solid rgba($p-warn, 0.32);

      > i { color: $p-warn-ink; }
    }

    &--info {
      background: $p-info-soft;
      border: 1px solid rgba($p-info, 0.28);

      > i { color: $p-info; }
    }
  }

  &__confirm {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 13px;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
    cursor: pointer;
  }
}
</style>
